import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Image as ImageIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { DashboardLayout } from '@/components/DashboardLayout';
import { PageCard } from '@/components/common/PageCard';
import { PageHint } from '@/components/common/PageHint';
import { PageToolbar } from '@components/common/PageToolbar';
import { UniversalPagination } from '@/components/common/UniversalPagination';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { ProPlanGate } from '@/components/common/ProPlanGate';
import { toastService } from '@/services/toast.service';
import { getImageUrl } from '@/utils/image-url.util';
import { fileService } from '@/services/file.service';
import { expertPostsService } from '@/services/expert-posts.service';
import { useExpertPlan } from '@/hooks/useExpertPlan';
import { useExpertPostOptions } from '@/hooks/useExpertPostOptions';
import { CreateExpertPostDto, ExpertPostI18n, ExpertPostManagementDto, ExpertPostVisibility, UpdateExpertPostDto } from '@/types/expert-posts';
import {
  ExpertPostFormDialog,
  initialPostFormData,
  PostFormData,
} from '@components/expert-posts/ExpertPostFormDialog';

export const ExpertPostsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasFeature, loading: planLoading } = useExpertPlan();
  const { visibilities, publicationStatuses } = useExpertPostOptions();
  const [posts, setPosts] = useState<ExpertPostManagementDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [publishedFilter, setPublishedFilter] = useState<string>('');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('');

  const getStatusLabel = (value: string, fallback: string) =>
    publicationStatuses.find((o) => o.value === value)?.label ?? fallback;

  const getVisibilityLabel = (value: string, fallback: string) =>
    visibilities.find((o) => o.value === value)?.label ?? fallback;

  const [openDialog, setOpenDialog] = useState(false);
  const [editingPost, setEditingPost] = useState<ExpertPostManagementDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<PostFormData>(initialPostFormData);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [postToDelete, setPostToDelete] = useState<ExpertPostManagementDto | null>(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const isPublished =
        publishedFilter === 'true' ? true : publishedFilter === 'false' ? false : undefined;
      const visibility = visibilityFilter ? (visibilityFilter as ExpertPostVisibility) : undefined;
      const response = await expertPostsService.getPosts({ page, limit, isPublished, visibility });
      setPosts(response.data);
      setTotal(response.meta.total);
    } catch {
      toastService.error(t('expertPosts.expert.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, limit, publishedFilter, visibilityFilter]);

  const handleOpenDialog = (post?: ExpertPostManagementDto) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        titleI18n: post.titleI18n,
        contentI18n: post.contentI18n,
        slug: post.slug ?? '',
        isPublished: post.isPublished,
        publishedAt: post.publishedAt ? new Date(post.publishedAt) : null,
        coverImageId: post.coverImageId ?? undefined,
        visibility: post.visibility,
      });
      setPreviewUrl(post.coverImageUrl ? getImageUrl(post.coverImageUrl) : null);
    } else {
      setEditingPost(null);
      setFormData(initialPostFormData);
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPost(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData(initialPostFormData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleRemoveCover = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData({ ...formData, coverImageId: null });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titleI18n.uk || !formData.contentI18n.uk) {
      toastService.error(t('expertPosts.expert.requiredFieldsError'));
      return;
    }
    setSubmitting(true);
    try {
      let coverImageId = formData.coverImageId;
      if (selectedFile) {
        const uploaded = await fileService.uploadFile(selectedFile, 'Post cover');
        coverImageId = uploaded.id;
      }

      if (editingPost) {
        const dto: UpdateExpertPostDto = {
          titleI18n: formData.titleI18n as ExpertPostI18n,
          contentI18n: formData.contentI18n as ExpertPostI18n,
          slug: formData.slug || undefined,
          isPublished: formData.isPublished,
          publishedAt: formData.publishedAt ? formData.publishedAt.toISOString() : undefined,
          coverImageId,
          visibility: formData.visibility,
        };
        await expertPostsService.updatePost(editingPost.id, dto);
        toastService.success(t('expertPosts.expert.updateSuccess'));
      } else {
        const dto: CreateExpertPostDto = {
          titleI18n: formData.titleI18n as ExpertPostI18n,
          contentI18n: formData.contentI18n as ExpertPostI18n,
          slug: formData.slug || undefined,
          isPublished: formData.isPublished,
          publishedAt: formData.publishedAt ? formData.publishedAt.toISOString() : undefined,
          coverImageId: coverImageId ?? undefined,
          visibility: formData.visibility,
        };
        await expertPostsService.createPost(dto);
        toastService.success(t('expertPosts.expert.createSuccess'));
      }
      handleCloseDialog();
      fetchPosts();
    } catch (err: any) {
      const backendMessage = err.response?.data?.message;
      const errorMessage = Array.isArray(backendMessage) ? backendMessage.join(', ') : backendMessage;
      toastService.error(errorMessage || t('expertPosts.expert.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!postToDelete) return;
    try {
      await expertPostsService.deletePost(postToDelete.id);
      toastService.success(t('expertPosts.expert.deleteSuccess'));
      fetchPosts();
    } catch {
      toastService.error(t('expertPosts.expert.deleteError'));
    } finally {
      setPostToDelete(null);
    }
  };

  return (
    <DashboardLayout data-i-doc={PageIndices.EXPERT_POSTS}>
      <Box>
        <PageHint iDoc={PageIndices.EXPERT_POSTS} />
        <ProPlanGate hasAccess={hasFeature('expert_posts')} loading={planLoading}>

        <PageCard>
          <PageToolbar
            filters={[
              {
                id: 'published',
                type: 'custom',
                renderCustom: () => (
                  <ToggleButtonGroup
                    value={publishedFilter}
                    exclusive
                    onChange={(_e, val) => { setPublishedFilter(val ?? ''); setPage(1); }}
                    size="small"
                  >
                    <ToggleButton value="">{t('expertPosts.expert.filters.all')}</ToggleButton>
                    <ToggleButton value="true">{getStatusLabel('true', t('expertPosts.expert.filters.published'))}</ToggleButton>
                    <ToggleButton value="false">{getStatusLabel('false', t('expertPosts.expert.filters.drafts'))}</ToggleButton>
                  </ToggleButtonGroup>
                ),
              },
              {
                id: 'visibility',
                type: 'custom',
                renderCustom: () => (
                  <ToggleButtonGroup
                    value={visibilityFilter}
                    exclusive
                    onChange={(_e, val) => { setVisibilityFilter(val ?? ''); setPage(1); }}
                    size="small"
                  >
                    <ToggleButton value="">{t('expertPosts.expert.filters.all')}</ToggleButton>
                    <ToggleButton value="clients">{getVisibilityLabel('clients', t('expertPosts.expert.filters.visibilityClients'))}</ToggleButton>
                    <ToggleButton value="website">{getVisibilityLabel('website', t('expertPosts.expert.filters.visibilityWebsite'))}</ToggleButton>
                  </ToggleButtonGroup>
                ),
              },
            ]}
            onReset={() => { setPublishedFilter(''); setVisibilityFilter(''); setPage(1); }}

            extraActions={
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                {t('expertPosts.expert.create')}
              </Button>
            }
          />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('expertPosts.expert.columns.id')}</TableCell>
                  <TableCell>{t('expertPosts.expert.columns.cover')}</TableCell>
                  <TableCell>{t('expertPosts.expert.columns.title')}</TableCell>
                  <TableCell>{t('expertPosts.expert.columns.status')}</TableCell>
                  <TableCell>{t('expertPosts.expert.columns.visibility')}</TableCell>
                  <TableCell>{t('expertPosts.expert.columns.publishedAt')}</TableCell>
                  <TableCell>{t('expertPosts.expert.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.map((post) => (
                  <TableRow
                    key={post.id}
                    hover
                    onDoubleClick={() => handleOpenDialog(post)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{post.id}</TableCell>
                    <TableCell>
                      {post.coverImageUrl ? (
                        <Avatar
                          variant="rounded"
                          src={getImageUrl(post.coverImageUrl)}
                          sx={{ width: 56, height: 56 }}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{post.titleI18n.uk}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={post.isPublished ? t('expertPosts.expert.statusPublished') : t('expertPosts.expert.statusDraft')}
                        color={post.isPublished ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          post.visibility === 'clients'
                            ? getVisibilityLabel('clients', t('expertPosts.expert.filters.visibilityClients'))
                            : post.visibility === 'website'
                              ? getVisibilityLabel('website', t('expertPosts.expert.filters.visibilityWebsite'))
                              : t('expertPosts.expert.filters.all')
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {post.publishedAt
                        ? format(new Date(post.publishedAt), 'dd.MM.yyyy HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenDialog(post)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => setPostToDelete(post)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {posts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t('expertPosts.expert.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={(val) => { setLimit(val); setPage(1); }}
            storageKey="pagination:expert-posts"
          />
        </PageCard>
        </ProPlanGate>
      </Box>

      <ExpertPostFormDialog
        open={openDialog}
        isEditing={!!editingPost}
        formData={formData}
        submitting={submitting}
        previewUrl={previewUrl}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onChange={setFormData}
        onFileChange={handleFileChange}
        onRemoveCover={handleRemoveCover}
      />

      <ConfirmationDialog
        open={!!postToDelete}
        onClose={() => setPostToDelete(null)}
        onConfirm={confirmDelete}
        title={t('expertPosts.expert.deleteConfirmTitle')}
        message={t('expertPosts.expert.deleteConfirmMessage', { title: postToDelete?.titleI18n.uk })}
        confirmText={t('expertPosts.expert.deleteConfirmAction')}
      />
    </DashboardLayout>
  );
};
